import React, { useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { Button, ButtonType } from '@/components/atoms/button'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { RadioButton } from '@/components/atoms/radio-button'
import dayjs from 'dayjs'
import { SearchReservationEachRoom } from '@/models/reservation'

interface Props {
  reservations: SearchReservationEachRoom[]
  onCancel: () => void
  onSubmit: (reservation: SearchReservationEachRoom) => void
}
export const ReservationRoomSelectorModal: React.FC<Props> = props => {
  const { t } = useTranslation()

  const [roomNumbersWithReservation, setRoomNumbersWithReservation] = useState<string[]>()
  const [targetReservation, setTargetReservation] = useState<SearchReservationEachRoom>()
  const [filterRoomNumber, setFilterRoomNumber] = useState<string>()

  useEffect(() => {
    const pickRoomNumbers: string[] = []
    props.reservations.forEach(r => {
      if (!r.roomNumber || pickRoomNumbers.includes(r.roomNumber)) {
        return
      }
      pickRoomNumbers.push(r.roomNumber)
    })
    setRoomNumbersWithReservation(pickRoomNumbers)
  }, [props.reservations])

  const selectableSearchReservations = useMemo(() => {
    if (!filterRoomNumber) {
      return props.reservations
    }
    return props.reservations?.filter(r => r.roomNumber === filterRoomNumber)
  }, [props.reservations, filterRoomNumber])

  return (
    <Modal customCss={modalStyle}>
      <ModalHeader customCss={modalHeaderStyle}>
        <div>{t('Search your reservation')}</div>
      </ModalHeader>
      <ModalBody customCss={modalBodyStyle}>
        <div style={{ display: 'flex', alignItems:'center', paddingBottom: 16, gap: 16 }}>
          <div css={inputTitleTextStyle}> {t('Room number')}</div>
          <Select
            customStyle={css({ paddingBottom: 0, width: 307 })}
            placeholder={t('Please select')}
            value={filterRoomNumber}
            onChange={e => {
              setTargetReservation(undefined)
              setFilterRoomNumber(e.toString())
            }}
          >
            {roomNumbersWithReservation?.map(num => (
              <AntSelect.Option key={num} value={num}>
                {num}
              </AntSelect.Option>
            ))}
          </Select>
        </div>
        <div css={reservationSearchListStyle}>
          <table>
            <thead>
              <tr>
                <th className="col-radio"></th>
                <th className="col-reservation-user">{t('Reservation person')}</th>
                <th className="col-reservation-guest">{t('Guest')}</th>
                <th className="col-guest-room">{t('Guest room')}</th>
                <th className="col-stay-date">{t('Date of stay')}</th>
              </tr>
            </thead>
            <tbody>
              {selectableSearchReservations?.map(r => {
                const rowKey = r.guestRoomAssignId || r.reservationId
                const selectedKey = targetReservation?.guestRoomAssignId || targetReservation?.reservationId
                const isSelected = rowKey === selectedKey
                return (
                  <tr key={rowKey} onClick={() => setTargetReservation(r)} className={isSelected ? 'selected' : ''}>
                    <td className="col-radio">
                      <RadioButton
                        id={r.guestRoomAssignId || r.reservationId}
                        checked={rowKey === selectedKey}
                        item={{ value: r.guestRoomAssignId || r.reservationId, label: '' }}
                        onChange={() => setTargetReservation(r)}
                        style={{ labelMargin: 0, radioSize: 18 }}
                      />
                    </td>
                    <td className="col-reservation-user">
                      <div className="text-kana">{r.reservationUserNameKana}</div>
                      <div className="text-name">{r.reservationUserName}</div>
                    </td>
                    <td className="col-reservation-guest">
                      <div className="text-kana">{r.reservationGuestNameKana}</div>
                      <div className="text-name">{r.reservationGuestName}</div>
                    </td>
                    <td className="col-guest-room">
                      <div className="text-name">
                        {r.roomTypeName}/{r.roomNumber}
                      </div>
                    </td>
                    <td className="col-stay-date">
                      <div className="text-name">{`${dayjs(r.checkinDate).format(t('MM-DD'))} ${r.nights}${t('Night')}`}</div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </ModalBody>
      <ModalFooter customCss={modalFooterStyle}>
        <Button buttonType={ButtonType.Secondary} height="36px" width="110px" marginRight={16} onClick={() => props.onCancel()}>
          {t('Back')}
        </Button>
        <Button
          buttonType={targetReservation ? ButtonType.Primary : ButtonType.PrimaryDisabled}
          height="36px"
          width="110px"
          marginRight={16}
          onClick={() => targetReservation && props.onSubmit(targetReservation)}
        >
          {t('Confirm')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const modalStyle = css({
  top: '5%',
  left: 'calc((100% - 1000px) / 2)',
  height: 555,
  minWidth: 1000,
  width: 1000,
})

const modalHeaderStyle = css({
  textAlign: 'center',
  color: '#272727',
  display: 'flex',
  justifyContent: 'space-between',
  height: 60,
})

const modalBodyStyle = css({
  padding: '24px 32px',
  height: 'calc(100% - 2px)',
  display: 'flex',
  flexDirection: 'column',
})

const modalFooterStyle = css({
  height: '60px',
  display: 'flex',
  justifyContent: 'end',
})

const inputTitleTextStyle = css({
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: '0.6px',
  color: '#676767',
})

const reservationSearchListStyle = css({
  flexGrow: 1,
  border: '1px solid #CCCCCC',
  borderRadius: 5,
  background: '#FFFFFF 0% 0% no-repeat padding-box',
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
  table: { borderCollapse: 'separate', width: '100%' },
  thead: {
    lineHeight: '32px',
    fontSize: 12,
    th: {
      borderBottom: '1px solid #F2F2F2',
      background: '#FFFFFF 0% 0% no-repeat padding-box',
      position: 'sticky',
      zIndex: 1,
      top: 0,
    },
  },
  tbody: {
    tr: {
      height: 64,
      lineHeight: '21px',
      cursor: 'pointer',
      td: {
        padding: '11px 0',
        alignContent: 'center',
        borderBottom: '1px solid #F2F2F2',
      },
      '.text-kana, .text-name': {
        width: 'inherit',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
      '.text-kana': {
        letterSpacing: 0.48,
        fontSize: 12,
        color: '#676767',
      },
      '.text-name': {
        letterSpacing: 0.56,
        fontSize: 14,
        color: '#272727',
      },
      ':hover': {
        boxShadow: '0px 3px 6px #0000000f',
      },
      '&.selected': {
        background: 'rgb(255, 252, 221)',
      },
    },
  },
  '.col-radio': { width: 56 },
  'td.col-radio': {
    display: 'flex',
    justifyContent: 'center',
    height: 65,
  },
  '.col-reservation-user': { width: 210 },
  '.col-reservation-guest': { width: 210 },
  '.col-stay-date': { width: 139 },
})
